import { Component, inject, OnInit, ViewChild, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { NgSelectComponent } from '@ng-select/ng-select';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AlertBoxComponent } from 'src/app/shared/components/alert-box/alert-box.component';
import { SpecialtiesService } from '../../services/specialties/specialties.service';
import { QuestionsService } from '../../services/questions.service';
import { Specialty, SpecialtyQueryParams } from '../../models/specialty.model';
import {
  AICheckStatus,
  AIReviewReason,
  AIReviewResult,
  QUESTION_STATUS_LABELS,
  Question,
  QuestionReviewAttempt,
  QuestionStatus,
} from '../../models/question.model';

// An attempt as shown on the page. Questions from before backend #1570 have no
// attempts, so one is built from the results stored on the question, which have
// no overall result, no threshold and so no relevance status.
interface AIReviewAttemptView extends Omit<QuestionReviewAttempt, 'result' | 'checks'> {
  result: AIReviewResult | null;
  checks: {
    image: { status: AICheckStatus };
    pii: { status: AICheckStatus };
    relevance: { status: AICheckStatus | null; score: number | null; threshold: number | null; relevant: boolean | null };
  };
  legacy: boolean;
}

const AI_REVIEW_REASON_LABELS: Record<AIReviewReason, string> = {
  last_attempt_rejected: 'السؤال وصل للمراجعة لأن الذكاء الاصطناعي رفض آخر محاولة',
  ai_unavailable: 'السؤال وصل للمراجعة لأن فحص الذكاء الاصطناعي لم يعمل',
};

const AI_RESULT_LABELS: Record<AIReviewResult, string> = {
  passed: 'مقبول',
  rejected: 'مرفوض',
  unavailable: 'لم يتم الفحص',
};

const AI_CHECK_LABELS: Record<AICheckStatus, string> = {
  passed: 'اجتياز',
  failed: 'فشل',
  not_run: 'لم يتم',
};

// Reuses the page's status colors: green passed, red failed, grey not run.
const AI_CHECK_CLASSES: Record<AICheckStatus, string> = {
  passed: 'status-green',
  failed: 'needs-review-status',
  not_run: 'closed-status',
};

const AI_RESULT_CLASSES: Record<AIReviewResult, string> = {
  passed: AI_CHECK_CLASSES.passed,
  rejected: AI_CHECK_CLASSES.failed,
  unavailable: AI_CHECK_CLASSES.not_run,
};

const ATTEMPT_CONTENT_COLLAPSE_LENGTH = 200;

@Component({
  selector: 'app-question-details',
  standalone: false,
  templateUrl: './question-details.component.html',
  styleUrls: ['./question-details.component.scss'],
})
export class QuestionDetailsComponent implements OnInit {
  questionId!: number;
  question: Question = { id: 0 };
  selectedSpecialization: number | null = null;
  specializations: Specialty[] = [];
  specializationsPage: number = 1;
  specializationsPageCount: number = 1;
  specializationsPageSize: number = 20;
  specializationsSearchInput!: string;
  saveDialogRef: MatDialogRef<AlertBoxComponent> | undefined;
  expandedAttempts = new Set<number>();
  reviewAttempts: AIReviewAttemptView[] = [];

  dialog = inject(MatDialog);
  private route = inject(ActivatedRoute);
  private specialtiesService = inject(SpecialtiesService);
  private questionsService = inject(QuestionsService);
  private destroyRef = inject(DestroyRef);

  @ViewChild('selectSpecializations') selectSpecializations!: NgSelectComponent;
  specializationsSearchTimeout: ReturnType<typeof setTimeout> | undefined;

  ngOnInit(): void {
    this.questionId = Number(this.route.snapshot.paramMap.get('id'));
    this.getSpecializations();
    this.getQuestion();
  }

  getQuestion(): void {
    this.questionsService.getQuestionById(this.questionId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response) => {
        this.question = response.data;
        this.reviewAttempts = this.buildReviewAttempts(response.data);
        this.selectedSpecialization = response.data.specialization?.id ?? null;
      },
      error: () => { /* keep placeholder question data on failure */ },
    });
  }

  getClientName(): string {
    const client = this.question.client;
    if (!client) return '-';
    return `${client.first_name} ${client.last_name}`.trim() || '-';
  }

  getStatusLabel(status?: string): string {
    if (!status) return '-';
    return QUESTION_STATUS_LABELS[status as QuestionStatus] || status;
  }

  private buildReviewAttempts(question: Question): AIReviewAttemptView[] {
    const attempts = question.ai_review?.attempts ?? [];
    if (attempts.length) {
      return attempts.map((attempt) => ({ ...attempt, legacy: false }));
    }

    const responses = question.ai_responses ?? [];
    const hasStoredResult =
      responses.length > 0 ||
      question.attachments_safety != null ||
      question.pii_safety != null ||
      question.relevance_safety != null;
    if (!hasStoredResult) return [];

    // When the AI did not answer, the old backend saved fail-closed values (pii failed,
    // relevance 0, images failed), so those checks are shown as not run.
    const textReview = responses.find((r) => r.check === 'text_review');
    const textNotRun = !!textReview && textReview.response == null;
    const imageReviews = responses.filter((r) => r.check === 'image_safety');
    const imagesNotRun = imageReviews.length > 0 && imageReviews.every((r) => r.response == null);
    const storedStatus = (value?: string | null, notRun = false): AICheckStatus =>
      notRun || (value !== 'passed' && value !== 'failed') ? 'not_run' : value;
    const score = textNotRun ? null : question.relevance_safety ?? null;

    return [
      {
        attempt: 0,
        created_at: question.created_at ?? '',
        content: question.content ?? '',
        has_image: !!question.attachments?.length,
        result: null,
        rejection_reasons: [],
        checks: {
          image: { status: storedStatus(question.attachments_safety, imagesNotRun) },
          pii: { status: storedStatus(question.pii_safety, textNotRun) },
          relevance: {
            status: score === null ? 'not_run' : null,
            score,
            threshold: null,
            relevant: textNotRun ? null : textReview?.response?.relevant ?? null,
          },
        },
        legacy: true,
      },
    ];
  }

  getReviewReasonLabel(): string | null {
    const reason = this.question.ai_review?.review_reason;
    return reason ? AI_REVIEW_REASON_LABELS[reason] ?? null : null;
  }

  getAttemptResultLabel(result: AIReviewResult): string {
    return AI_RESULT_LABELS[result] ?? result;
  }

  getAttemptResultClass(result: AIReviewResult): string {
    return AI_RESULT_CLASSES[result] ?? '';
  }

  getCheckLabel(status: AICheckStatus): string {
    return AI_CHECK_LABELS[status] ?? status;
  }

  getCheckClass(status: AICheckStatus): string {
    return AI_CHECK_CLASSES[status] ?? '';
  }

  getRelevantLabel(relevant: boolean | null): string {
    if (relevant === null) return '-';
    return relevant ? 'متعلق بالعقار' : 'غير متعلق بالعقار';
  }

  isAttemptContentLong(attempt: AIReviewAttemptView): boolean {
    return (attempt.content?.length ?? 0) > ATTEMPT_CONTENT_COLLAPSE_LENGTH;
  }

  toggleAttemptContent(attemptNumber: number): void {
    if (this.expandedAttempts.has(attemptNumber)) {
      this.expandedAttempts.delete(attemptNumber);
    } else {
      this.expandedAttempts.add(attemptNumber);
    }
  }

  getStatusClass(status?: string): string {
    if (!status) return '';
    const statusLower = status.toLowerCase();

    if (statusLower === QuestionStatus.NeedReview || statusLower.includes('review') || statusLower.includes('مراجعة')) {
      return 'needs-review-status';
    } else if (statusLower === QuestionStatus.Closed || statusLower.includes('مغلق')) {
      return 'closed-status';
    } else if (statusLower === QuestionStatus.Open || statusLower.includes('مفتوح') || statusLower.includes('اجاب')) {
      return 'open-status';
    }
    return 'open-status';
  }

  isQuestionClosed(): boolean {
    return this.getStatusClass(this.question.status) === 'closed-status';
  }

  isQuestionOpen(): boolean {
    return this.getStatusClass(this.question.status) === 'open-status';
  }

  getSpecializations(): void {
    const payload: SpecialtyQueryParams = {
      page: this.specializationsPage,
      page_size: this.specializationsPageSize,
    };
    if (this.specializationsSearchInput) {
      payload['search'] = this.specializationsSearchInput;
    }
    this.specialtiesService.getSpecialties(payload).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response) => {
        this.specializations = [...this.specializations, ...(response.data ?? [])];
        this.specializationsPageCount = response.pages_count ?? 1;
      },
      error: () => {
        this.specializations = [];
        this.specializationsPageCount = 1;
      },
    });
  }

  onScrollSpecializationsPagination(): void {
    if (this.specializationsPage < this.specializationsPageCount) {
      this.specializationsPage++;
      this.getSpecializations();
    }
  }

  resetSpecializationsSearch(): void {
    this.specializations = [];
    this.specializationsPage = 1;
    this.specializationsSearchInput = '';
    this.getSpecializations();
  }

  searchSpecializationsText(): void {
    this.specializationsSearchInput = this.selectSpecializations.searchTerm;
    this.specializations = [];
    this.specializationsPage = 1;
    clearTimeout(this.specializationsSearchTimeout);
    let that = this;
    that.specializationsSearchTimeout = setTimeout(() => {
      if (that.specializationsSearchInput != '') {
        that.getSpecializations();
      } else {
        that.specializationsPage = 1;
        that.getSpecializations();
      }
    }, 600);
  }

  onSpecializationChange(specializationId: number): void {
    const previousSpecializationId = this.question.specialization?.id;

    this.questionsService.updateQuestion(this.questionId, { specialization: specializationId }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response) => {
        console.log('updateQuestion response:', response);
        // Use the already-loaded specializations list rather than the PUT response,
        // since the write endpoint isn't confirmed to echo back the expanded {id, name_ar, name_en} object.
        this.question.specialization = this.specializations.find((s) => s.id === specializationId);
      },
      error: () => {
        this.selectedSpecialization = previousSpecializationId ?? null;
        this.showAlertDialog(false, 'حدث خطأ أثناء حفظ التخصص');
      },
    });
  }

  acceptQuestion(): void {
    this.updateStatus(QuestionStatus.Open, 'حدث خطأ أثناء قبول السؤال');
  }

  closeQuestion(): void {
    this.updateStatus(QuestionStatus.Closed, 'حدث خطأ أثناء إغلاق السؤال');
  }

  private updateStatus(status: QuestionStatus, errorMessage: string): void {
    const previousStatus = this.question.status;

    this.questionsService.updateQuestion(this.questionId, { status }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        // Use the known label instead of the PUT response, since its shape for `status` isn't confirmed.
        this.question.status = QUESTION_STATUS_LABELS[status];
      },
      error: () => {
        this.question.status = previousStatus;
        this.showAlertDialog(false, errorMessage);
      },
    });
  }

  private showAlertDialog(success: boolean, message: string): void {
    this.dialog.open(AlertBoxComponent, {
      data: {
        imgSuccess: success,
        message,
        button: 'تم',
      },
    });
  }

  copyClientMobile() {
    const mobile = this.question.client?.mobile;
    if (!mobile) return;
    navigator.clipboard.writeText(mobile).then(() => {
      this.saveDialogRef = this.dialog.open(AlertBoxComponent, {
        data: {
          imgSuccess: true,
          message: 'تم نسخ الرابط',
          button: 'تم',
        },
      });
    });
  }

}
